export const METERS_TO_FEET = 3.28084;
export const METERS_TO_INCHES = 39.3701;

// Area conversion factor (squared linear factor). Floor area is computed
// (shoelace) and stored as m² on the backend (see ADR 0006); this converts it
// for display only — the frontend never computes area itself. Imperial area is
// always ft² (see AREA_CONFIG), so there is no m²→in² factor.
export const METERS2_TO_FEET2 = METERS_TO_FEET * METERS_TO_FEET;

export const UNIT_SYSTEMS = Object.freeze({
    METERS: 'meters',
    FEET: 'feet',
    INCHES: 'inches',
});

const UNIT_CONFIG = {
    [UNIT_SYSTEMS.METERS]: {
        label: 'Meters',
        suffix: 'm',
        decimals: 1,
        multiplier: 1,
    },
    [UNIT_SYSTEMS.FEET]: {
        label: 'Feet',
        suffix: 'ft',
        decimals: 1,
        multiplier: METERS_TO_FEET,
    },
    [UNIT_SYSTEMS.INCHES]: {
        label: 'Inches',
        suffix: 'in',
        decimals: 0,
        multiplier: METERS_TO_INCHES,
    },
};

export const getUnitSystemOptions = () => (
    Object.values(UNIT_SYSTEMS).map((unitSystem) => ({
        value: unitSystem,
        label: UNIT_CONFIG[unitSystem].suffix,
        ariaLabel: `Display dimensions in ${unitSystem}`,
    }))
);

export const normalizeUnitSystem = (unitSystem) => (
    UNIT_CONFIG[unitSystem] ? unitSystem : UNIT_SYSTEMS.METERS
);

const getUnitConfig = (unitSystem) => UNIT_CONFIG[normalizeUnitSystem(unitSystem)];

export const getUnitSystemLabel = (unitSystem) => getUnitConfig(unitSystem).label;

export const getUnitSystemSuffix = (unitSystem) => getUnitConfig(unitSystem).suffix;

export const convertMetersValue = (valueInMeters, unitSystem) => {
    const numericValue = typeof valueInMeters === 'number' && !Number.isNaN(valueInMeters)
        ? valueInMeters
        : 0;

    return numericValue * getUnitConfig(unitSystem).multiplier;
};

export const convertMeterDimensions = (dimensionsInMeters, unitSystem) => (
    [0, 1, 2].map((index) => convertMetersValue(dimensionsInMeters?.[index], unitSystem))
);

const formatConvertedValue = (value, unitSystem) => {
    const { decimals } = getUnitConfig(unitSystem);
    return value.toFixed(decimals);
};

// Convert a meter value and format it with the unit's canonical decimal
// places (no suffix). Public so display sites (e.g. floor footprint rows)
// don't re-derive the per-unit decimal count inline. Returns "0" for
// null/NaN via convertMetersValue's fallback.
export const formatLinearMeasurement = (valueInMeters, unitSystem) => (
    formatConvertedValue(convertMetersValue(valueInMeters, unitSystem), normalizeUnitSystem(unitSystem))
);

export const formatDimensionTriplet = (dimensionsInMeters, unitSystem) => {
    const normalizedUnitSystem = normalizeUnitSystem(unitSystem);
    const convertedDimensions = convertMeterDimensions(dimensionsInMeters, normalizedUnitSystem);
    const suffix = getUnitSystemSuffix(normalizedUnitSystem);

    return `${convertedDimensions.map((value) => formatConvertedValue(value, normalizedUnitSystem)).join(' × ')} ${suffix}`;
};

// Area display tracks the unit SYSTEM, not the exact linear unit: metric → m²,
// imperial → ft². Both `feet` AND `inches` map to ft² on purpose — square
// inches (~17,700 in² for a small room) is never a useful figure, and a room's
// area in ft² is the universal convention. This is intentional and means a row
// can legitimately read "123.0 ft² · Width 209in · Depth 95in": the area uses
// the imperial AREA unit while the lengths use the chosen linear unit. Callers
// must source the area suffix from `getAreaSuffix` (not the linear
// `getUnitSystemSuffix`) so the two stay independently correct.
const AREA_CONFIG = {
    [UNIT_SYSTEMS.METERS]: { suffix: 'm²', decimals: 1, multiplier: 1 },
    [UNIT_SYSTEMS.FEET]: { suffix: 'ft²', decimals: 1, multiplier: METERS2_TO_FEET2 },
    [UNIT_SYSTEMS.INCHES]: { suffix: 'ft²', decimals: 1, multiplier: METERS2_TO_FEET2 },
};

const getAreaConfig = (unitSystem) => AREA_CONFIG[normalizeUnitSystem(unitSystem)];

// The area unit suffix (m² / ft²) for a unit system. Use this for area labels
// instead of getUnitSystemSuffix, which returns the LINEAR unit (m / ft / in).
export const getAreaSuffix = (unitSystem) => getAreaConfig(unitSystem).suffix;

// Convert a backend-supplied area (m²) into the target unit system. Returns 0
// for null/NaN so callers never render "NaN ft²".
export const convertMeterArea = (areaInMeters2, unitSystem) => {
    const numericValue = typeof areaInMeters2 === 'number' && !Number.isNaN(areaInMeters2)
        ? areaInMeters2
        : 0;
    return numericValue * getAreaConfig(unitSystem).multiplier;
};

// Format a backend-supplied area (m²) as e.g. "149 ft²". Display only — the
// value is computed once on the backend (ADR 0006).
export const formatArea = (areaInMeters2, unitSystem) => {
    const { decimals, suffix } = getAreaConfig(unitSystem);
    return `${convertMeterArea(areaInMeters2, unitSystem).toFixed(decimals)} ${suffix}`;
};

// Format a meter length with its unit suffix, e.g. "0.5m" / "20in". Thin
// wrapper over formatLinearMeasurement so display sites don't re-append the
// suffix by hand. Returns null for null/undefined so callers can skip a row.
export const formatLengthWithSuffix = (valueInMeters, unitSystem) => {
    if (valueInMeters === null || valueInMeters === undefined) return null;
    const u = normalizeUnitSystem(unitSystem);
    return `${formatLinearMeasurement(valueInMeters, u)}${getUnitSystemSuffix(u)}`;
};

// Backend `wall_measurements[wallUuid]` is computed server-side (services/
// wall_geometry.py) and only formatted here — same display-only contract as
// floor area (ADR 0006). The helpers below turn one wall's block into strings
// for the list panel and into label descriptors for the 3D scene.

// Terse derived-measurement lines for one opening (door/window) row in the
// list: the gap to the previous opening on the same wall, the distance to the
// nearest wall corner, and the sill height. Skips any field the backend
// couldn't resolve (null) AND any zero-length value — a zero gap is two
// touching openings, a zero corner distance is a flush opening, and doors
// carry sill_m = 0 by design ("Sill 0.0m" on every door is noise). The same
// > 0 gate the 3D labels use (buildOpeningSpacingLabels), so the list and
// the scene always agree on what's worth showing. Returns [] when nothing is.
export const describeOpeningSpacing = (opening, unitSystem) => {
    if (!opening || typeof opening !== 'object') return [];
    const parts = [];
    if (Number.isFinite(opening.gap_to_prev_m) && opening.gap_to_prev_m > 0) {
        parts.push(`Gap ${formatLengthWithSuffix(opening.gap_to_prev_m, unitSystem)}`);
    }

    // Distance to the nearest wall corner — "how far from the wall" the user
    // asked for. Prefer whichever edge is closer; both may be present.
    const edges = [opening.edge_to_start_m, opening.edge_to_end_m].filter(Number.isFinite);
    if (edges.length > 0) {
        const nearest = Math.min(...edges);
        if (nearest > 0) parts.push(`Corner ${formatLengthWithSuffix(nearest, unitSystem)}`);
    }

    if (Number.isFinite(opening.sill_m) && opening.sill_m > 0) {
        parts.push(`Sill ${formatLengthWithSuffix(opening.sill_m, unitSystem)}`);
    }
    return parts;
};

// Label descriptors for the spaces BETWEEN openings on one wall (and between
// the end openings and the wall corners), for placement in the 3D scene.
// Returns [{ key, alongM, text }] where `alongM` is the distance from the
// wall's start along its length; the caller maps that to a local-plane X
// (alongM − wallWidth/2) inside the wall's transform group — the same frame
// the edge labels use. Only positive spans produce a label (a zero gap is
// two touching openings, not a measurement worth showing).
export const buildOpeningSpacingLabels = (openings, wallWidthM, unitSystem) => {
    if (!Array.isArray(openings) || !Number.isFinite(wallWidthM)) return [];
    const labels = [];
    // The rightmost trailing edge seen so far. A running MAX (mirroring the
    // backend's gap math): an opening nested inside a wider earlier one must
    // not pull the frontier back, or the next gap label lands mid-opening.
    let prevTrailing = null;
    openings.forEach((op, i) => {
        const leading = op?.along_offset_m;
        if (leading === null || leading === undefined) return;
        const width = Number.isFinite(op.width_m) ? op.width_m : 0;
        const trailing = leading + width;

        // Distance from the wall's start corner to the first opening.
        if (prevTrailing === null && Number.isFinite(op.edge_to_start_m) && op.edge_to_start_m > 0) {
            labels.push({
                key: `edge-start-${i}`,
                alongM: op.edge_to_start_m / 2,
                text: formatLengthWithSuffix(op.edge_to_start_m, unitSystem),
            });
        }
        // Gap to the previous opening on this wall.
        if (prevTrailing !== null && Number.isFinite(op.gap_to_prev_m) && op.gap_to_prev_m > 0) {
            labels.push({
                key: `gap-${i}`,
                alongM: (prevTrailing + leading) / 2,
                text: formatLengthWithSuffix(op.gap_to_prev_m, unitSystem),
            });
        }
        prevTrailing = prevTrailing === null ? trailing : Math.max(prevTrailing, trailing);
    });

    // Distance from the wall's end corner to the SPATIALLY rightmost opening
    // — the one with the maximum trailing edge, which is not necessarily the
    // last by leading order (a wide earlier opening can extend past it).
    // prevTrailing already holds that maximum from the loop above.
    if (prevTrailing !== null) {
        const endSpan = wallWidthM - prevTrailing;
        if (endSpan > 0) {
            labels.push({
                key: 'edge-end',
                alongM: (prevTrailing + wallWidthM) / 2,
                text: formatLengthWithSuffix(endSpan, unitSystem),
            });
        }
    }
    return labels;
};

// Tolerance for "approximately zero" in wall-local coordinates (meters).
// Sub-millimeter wobble in the polygon corners is noise, not a real edge
// direction change.
const EDGE_AXIS_EPSILON = 1e-3;

const isValidPolygon = (polygonCorners) => (
    Array.isArray(polygonCorners) && polygonCorners.length >= 3
);

/**
 * Synthesize a 4-corner rectangle from a wall's `dimensions` [w, h, _].
 *
 * RoomPlan ships `polygonCorners` populated only for walls whose outline
 * isn't a plain rectangle (gable walls under sloped ceilings, L-shapes,
 * etc.). For simple rectangular walls it ships an empty array, since the
 * shape is fully implied by `dimensions`. The renderer and the list row
 * both want to operate on a polygon uniformly — call this to reconstruct
 * the implied rectangle so the same `classifyWallEdges` / `formatWallEdges`
 * pipeline produces consistent output for every wall.
 *
 * The synthesized polygon is centered on (0, 0) in the wall's local plane
 * to match the convention RoomPlan uses for its real polygons — the
 * parent transform places the rectangle in world space either way.
 *
 * Returns null for invalid input (missing dimensions or non-positive
 * width/height) so callers can short-circuit safely.
 */
export const inferRectanglePolygon = (dimensions) => {
    if (!Array.isArray(dimensions) || dimensions.length < 2) return null;
    const [w, h] = dimensions;
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
    const halfW = w / 2;
    const halfH = h / 2;
    return [
        [-halfW, -halfH],
        [halfW, -halfH],
        [halfW, halfH],
        [-halfW, halfH],
    ];
};

// Classify a single edge by its direction in the wall's local 2D plane.
// Returns one of: 'vertical' | 'horizontal' | 'sloped'.
const classifyEdgeDirection = (dx, dy) => {
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    if (absX < EDGE_AXIS_EPSILON) return 'vertical';
    if (absY < EDGE_AXIS_EPSILON) return 'horizontal';
    return 'sloped';
};

// Detect a plain axis-aligned rectangle (4 corners, all edges either
// purely vertical or purely horizontal). For these, the list row is more
// readable as a deduplicated "Width X · Height Y" than as four labeled
// edges that just repeat the same two numbers.
const isAxisAlignedRectangle = (edges) => {
    if (edges.length !== 4) return false;
    return edges.every(e => e.direction === 'vertical' || e.direction === 'horizontal');
};

/**
 * Classify each edge of a wall polygon by orientation and assign
 * human-readable labels (Tall, Short, Slope, Top, Floor).
 *
 * Returns an array of edges in polygon order, each with:
 *   { from, to, midpoint, length, direction, label, angleDeg? }
 *
 * `from`, `to`, `midpoint` are 2D points in the wall's local plane
 * (the wall's transform places them in world space — that math is the
 * caller's job, not ours).
 *
 * Labels are assigned semantically:
 *   - The longest vertical edge becomes "Tall"; the shortest (when there
 *     are at least 2 verticals of different length) becomes "Short";
 *     any others are "Side".
 *   - The lowest horizontal edge (smallest local Y) is "Floor"; any
 *     other horizontals are "Top".
 *   - Sloped edges are "Slope" and carry `angleDeg` (from horizontal).
 *
 * Returns null for any input that can't form a polygon (< 3 corners).
 */
export const classifyWallEdges = (polygonCorners) => {
    if (!isValidPolygon(polygonCorners)) return null;

    const edges = polygonCorners.map((corner, i) => {
        const next = polygonCorners[(i + 1) % polygonCorners.length];
        const dx = next[0] - corner[0];
        const dy = next[1] - corner[1];
        const length = Math.sqrt(dx * dx + dy * dy);
        const direction = classifyEdgeDirection(dx, dy);
        const midpoint = [
            (corner[0] + next[0]) / 2,
            (corner[1] + next[1]) / 2,
        ];
        const edge = { from: corner, to: next, midpoint, length, direction };
        if (direction === 'sloped') {
            edge.angleDeg = Math.abs(Math.atan2(dy, dx) * 180 / Math.PI);
            if (edge.angleDeg > 90) edge.angleDeg = 180 - edge.angleDeg;
        }
        return edge;
    });

    // Assign semantic labels. The classification is order-independent —
    // it depends only on each edge's direction and length relative to
    // its siblings.
    const verticals = edges.filter(e => e.direction === 'vertical');
    const horizontals = edges.filter(e => e.direction === 'horizontal');

    const tallest = verticals.length ? Math.max(...verticals.map(e => e.length)) : null;
    const shortest = verticals.length ? Math.min(...verticals.map(e => e.length)) : null;
    const lowestY = horizontals.length
        ? Math.min(...horizontals.map(e => Math.min(e.from[1], e.to[1])))
        : null;

    for (const edge of edges) {
        if (edge.direction === 'vertical') {
            // Only call something "Tall" or "Short" if there's actual
            // variation; for a plain rectangle (two equal verticals) both
            // are just "Side" — the rectangle helper handles the better
            // "Width × Height" display anyway.
            if (tallest !== shortest && edge.length === tallest) edge.label = 'Tall';
            else if (tallest !== shortest && edge.length === shortest) edge.label = 'Short';
            else edge.label = 'Side';
        } else if (edge.direction === 'horizontal') {
            const edgeY = Math.min(edge.from[1], edge.to[1]);
            edge.label = (edgeY <= lowestY + EDGE_AXIS_EPSILON) ? 'Floor' : 'Top';
        } else {
            edge.label = 'Slope';
        }
    }

    return edges;
};

/**
 * Format a wall's edges as a terse single-line string for the list row.
 * Edges are emitted in the order RoomPlan listed them in the polygon
 * (NOT re-sorted by label priority — that matters for the JSDoc example).
 *
 *   Pentagon under stairs: "Slope 3.2m (37°) · Top 0.2m · Tall 2.7m · Floor 2.8m · Short 0.8m"
 *   Plain rectangle:       "Width 4.5m · Height 1.4m" (deduplicated)
 *
 * Returns null for inputs that can't be classified — callers should fall
 * back to `formatDimensionTriplet`.
 */
export const formatWallEdges = (polygonCorners, unitSystem) => {
    const edges = classifyWallEdges(polygonCorners);
    if (!edges) return null;

    const normalizedUnitSystem = normalizeUnitSystem(unitSystem);
    const suffix = getUnitSystemSuffix(normalizedUnitSystem);

    const fmt = (lengthMeters) => (
        formatConvertedValue(convertMetersValue(lengthMeters, normalizedUnitSystem), normalizedUnitSystem)
    );

    // Plain rectangle shortcut: collapse the four "Side/Side/Floor/Top"
    // edges into the two unique magnitudes the user actually cares about.
    if (isAxisAlignedRectangle(edges)) {
        const verticalLen = edges.find(e => e.direction === 'vertical')?.length ?? 0;
        const horizontalLen = edges.find(e => e.direction === 'horizontal')?.length ?? 0;
        return `Width ${fmt(horizontalLen)}${suffix} · Height ${fmt(verticalLen)}${suffix}`;
    }

    return edges.map((edge) => {
        const lenStr = `${fmt(edge.length)}${suffix}`;
        if (edge.direction === 'sloped') {
            return `${edge.label} ${lenStr} (${Math.round(edge.angleDeg)}°)`;
        }
        return `${edge.label} ${lenStr}`;
    }).join(' · ');
};
